import { LeftOutlined } from '@ant-design/icons'
import {
  App,
  Button,
  Flex,
  Space,
  Table,
} from 'antd'
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react'

import { Role } from '@/model/role'
import { updateRole } from '@/redux/role'
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import styles from '../settings.module.css'

import useSetPermissionsColumns from './hooks/use-set-permissions-columns'
import useSetPermissionsSelection from './hooks/use-set-permissions-selection'
import roleToTableData, { DataType } from './role-to-table-data'

export interface SetPermissionsProps {
  role: Role;
  onBack?: () => void;
  onSave?: () => void;
}

const selector = createAppSelector(
  [
    (state) => state.menu.menus,
    (state) => state.functionPermission.functionPermissions,
  ],
  (menus, functionPermissions) => ({
    menus,
    functionPermissions,
  }),
)

export default function SetPermissions(
  { role: defaultRole, onBack, onSave }: SetPermissionsProps,
) {
  const { functionPermissions, menus } = useAppSelector(selector)
  const [role, setRole] = useState(defaultRole)
  const [savingLoading, setSavingLoading] = useState(false)

  const { message } = App.useApp()
  const dispatch = useAppDispatch()

  const selectedRowKeys = useMemo(() => {
    return role.menuPermissions.map((m) => m.menuId)
      .filter(id => {
        const children = menus.filter(m => m.parentId === id)
        const allChildrenSelected = children
          .every(m => role.menuPermissions.some(
            mp => mp.menuId === m.id))

        // if all children are selected, then the parent is selected
        // if there are no children, then the parent is selected
        // 0 is not a valid menu id
        return id !== 0 && ((children.length && allChildrenSelected) || children.length === 0)
      })
  }, [menus, role.menuPermissions])

  const onSavePermissions = useCallback(async () => {
    setSavingLoading(true)
    await dispatch(updateRole(role)).unwrap()
    void message.success('Permissions saved')
    onSave?.()
    setSavingLoading(false)
  }, [dispatch, message, onSave, role])

  const tableData: DataType[] = useMemo(
    () => roleToTableData(menus, functionPermissions, role),
    [menus, functionPermissions, role],
  )

  const columns = useSetPermissionsColumns({
    role,
    setRole,
    functionPermissions,
    selectedRowKeys,
  })

  const { expandable, rowSelection } = useSetPermissionsSelection({
    role,
    setRole,
    functionPermissions,
    selectedRowKeys,
  })

  return (
    <Flex vertical className={styles.settingsSection}>
      <Flex className="settings-content-header">
        <Button type="text" icon={<LeftOutlined />} onClick={() => onBack?.()}>
          Set permissions for {role.name}
        </Button>
        <Space>
          <Button
            type="primary"
            loading={savingLoading}
            onClick={onSavePermissions}
          >
            Save
          </Button>
        </Space>
      </Flex>
      <div className="settings-content">
        <Table
          pagination={false}
          columns={columns}
          expandable={expandable}
          rowSelection={rowSelection}
          dataSource={tableData}
        />
      </div>
    </Flex>
  )
}
